import { useState } from 'react';
import { ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';

export function ForgotPassword({ onBackToSignIn }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { sendReset } = useAuth();

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      await sendReset(email);
    } catch (error) {
      // Enumeration-safe behavior: return the same UI outcome even when the
      // auth provider rejects unknown or invalid accounts.
      console.warn('Password reset request returned non-blocking error:', error);
    } finally {
      setIsSuccess(true);
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Brand Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent mb-2">
              MealPlan Genie
            </h1>
          </div>

          {/* Success Message */}
          <div className="bg-white rounded-3xl shadow-sm p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              
              <h2 className="text-2xl font-semibold mb-3">Check Your Email</h2>
              
              <p className="text-[#757575] mb-2">
                If an account exists for this email, you will receive a password reset email shortly:
              </p>
              <p className="text-gray-900 font-medium mb-6">
                {email}
              </p>
              
              <p className="text-sm text-[#757575] mb-8">
                Check your inbox and spam/junk folder. If you still do not receive an email after a few minutes, check for typos and try again.
              </p>
              
              <button
                onClick={onBackToSignIn}
                className="w-full py-3 bg-primary text-[#2D3319] rounded-full hover:shadow-lg transition-all font-medium"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logo className="w-16 h-16" color="#AFBB8B" />
          </div>
          <h1 className="text-4xl font-bold text-[#6B7A54] mb-2">
            MealPlan Genie
          </h1>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-white rounded-3xl shadow-sm p-8">
          {/* Back Button */}
          <button
            onClick={onBackToSignIn}
            className="flex items-center gap-2 text-[#B85A5A] hover:text-[#A04848] transition-colors mb-6 underline"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sign In</span>
          </button>

          <h2 className="text-xl font-semibold mb-2">Forgot Password?</h2>
          <p className="text-sm text-[#757575] mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
                className={`w-full px-4 py-3 rounded-2xl border ${
                  error ? 'border-[#B85A5A]' : 'border-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors`}
                placeholder="you@example.com"
              />
              {error && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span className="text-sm text-[#B85A5A]">{error}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-primary text-[#2D3319] rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}